import { useEffect, useState } from 'react';
import { Plus, Trash2, Save, Pencil, X } from 'lucide-react';
import api from '../../api';
import { useToast } from '../../hooks/use-toast';

const EMPTY = {
  city_fr: '', city_en: '', city_zh: '',
  date_fr: '', date_en: '', date_zh: '',
  focus_fr: '', focus_en: '', focus_zh: '',
  order: 0,
};

export default function MissionsManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // id or 'new'
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get('/admin/missions');
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);

  const startEdit = (m) => {
    setEditing(m.id);
    setForm({
      city_fr: m.city.fr || '', city_en: m.city.en || '', city_zh: m.city.zh || '',
      date_fr: m.date.fr || '', date_en: m.date.en || '', date_zh: m.date.zh || '',
      focus_fr: m.focus.fr || '', focus_en: m.focus.en || '', focus_zh: m.focus.zh || '',
      order: m.order || 0,
    });
  };

  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing === 'new') await api.post('/admin/missions', form);
      else await api.put(`/admin/missions/${editing}`, form);
      toast({ title: 'Mission enregistrée' });
      cancel(); load();
    } catch (err) {
      console.error('MissionsManager save error', err?.response || err);
      const detail = err?.response?.data?.detail;
      const detailStr = Array.isArray(detail) ? detail.map((d) => `${d.loc?.slice(-1)[0]}: ${d.msg}`).join(' • ') : (detail || err?.message || 'Enregistrement impossible');
      toast({ title: `Erreur ${err?.response?.status || ''}`.trim(), description: detailStr, duration: 15000 });
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Supprimer cette mission ?')) return;
    await api.delete(`/admin/missions/${id}`);
    load();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-brand-navy">Missions programmées ({items.length})</h2>
          <button onClick={() => { setEditing('new'); setForm(EMPTY); }} className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-brand-navy text-white text-xs font-semibold">
            <Plus className="w-3.5 h-3.5" /> Nouvelle mission
          </button>
        </div>
        {items.length === 0 ? <p className="text-sm text-gray-400 italic">Aucune mission.</p> : (
          <ul className="space-y-2 max-h-[500px] overflow-y-auto">
            {items.map((m) => (
              <li key={m.id} className="p-3 rounded border border-gray-100 flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-semibold text-brand-navy text-sm">{m.city.fr} <span className="text-xs text-gray-500">• {m.date.fr}</span></div>
                  <div className="text-xs text-gray-500 mt-1">{m.focus.fr}</div>
                  <div className="text-[10px] text-gray-400 mt-1">EN: {m.city.en} · ZH: {m.city.zh}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => startEdit(m)} className="p-1.5 rounded hover:bg-gray-100"><Pencil className="w-4 h-4 text-brand-navy" /></button>
                  <button onClick={() => del(m.id)} className="p-1.5 rounded hover:bg-red-50"><Trash2 className="w-4 h-4 text-red-600" /></button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-brand-navy">{editing === 'new' ? 'Nouvelle mission' : editing ? 'Modifier' : 'Sélectionner une mission'}</h2>
          {editing && <button onClick={cancel} className="text-xs text-gray-500 hover:text-brand-navy flex items-center gap-1"><X className="w-3.5 h-3.5" /> Annuler</button>}
        </div>
        {!editing ? <p className="text-sm text-gray-400 italic">Sélectionnez une mission à modifier ou créez-en une nouvelle.</p> : (
          <form onSubmit={save} className="space-y-3">
            <TriInput label="Ville" fr={form.city_fr} en={form.city_en} zh={form.city_zh}
              onChange={(v) => setForm({ ...form, ...v })} keys={['city_fr', 'city_en', 'city_zh']} />
            <TriInput label="Dates" fr={form.date_fr} en={form.date_en} zh={form.date_zh}
              onChange={(v) => setForm({ ...form, ...v })} keys={['date_fr', 'date_en', 'date_zh']} />
            <TriInput label="Thème / Focus" fr={form.focus_fr} en={form.focus_en} zh={form.focus_zh}
              onChange={(v) => setForm({ ...form, ...v })} keys={['focus_fr', 'focus_en', 'focus_zh']} />
            <div>
              <label className="block text-xs font-semibold text-brand-navy uppercase mb-1">Ordre d'affichage</label>
              <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-200 rounded" />
            </div>
            <button disabled={busy} className="btn-yellow w-full justify-center disabled:opacity-60"><Save className="w-4 h-4" /> Enregistrer</button>
          </form>
        )}
      </div>
    </div>
  );
}

export function TriInput({ label, fr, en, zh, onChange, keys, textarea = false }) {
  const Tag = textarea ? 'textarea' : 'input';
  const commonCls = 'w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-brand-yellow';
  return (
    <div>
      <label className="block text-xs font-semibold text-brand-navy uppercase mb-1">{label}</label>
      <div className="grid grid-cols-3 gap-2">
        <div><div className="text-[10px] text-gray-500 mb-1">FR</div>
          <Tag {...(textarea ? { rows: 2 } : {})} value={fr} onChange={(e) => onChange({ [keys[0]]: e.target.value })} className={commonCls} /></div>
        <div><div className="text-[10px] text-gray-500 mb-1">EN</div>
          <Tag {...(textarea ? { rows: 2 } : {})} value={en} onChange={(e) => onChange({ [keys[1]]: e.target.value })} className={commonCls} /></div>
        <div><div className="text-[10px] text-gray-500 mb-1">中文</div>
          <Tag {...(textarea ? { rows: 2 } : {})} value={zh} onChange={(e) => onChange({ [keys[2]]: e.target.value })} className={commonCls} /></div>
      </div>
    </div>
  );
}
